/* =============================================================
   VESSELS — DATA ACCESS
   =============================================================
   The sibling of berths.ts. The table is owner, identity, type
   and the three dimensions; docs/data-model.md documents `flag`
   and the service needs, which no migration has added yet — see
   the head of supabase/migrations/20260919090628_create_vessels.

   Every query names its columns, as everywhere else here.

   ONE THING TO KNOW ABOUT THE READS: unlike berths, this table
   is not readable by anon at all, and a signed-in customer sees
   only their own company's fleet. The panel gets everything
   because staff hold a platform_admins row. A list that comes
   back empty from a browser is far more likely to be a missing
   grant than a missing fleet.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import { AMENITY_ORDER } from './amenities'
import type { Vessel, VesselInput } from './types'

/* `company:companies(id, name)` is the same PostgREST embed berths.ts uses,
 * resolved through the company_id foreign key, and it carries the same two
 * caveats: the alias keeps the property singular, and every query using it
 * ends in `.overrideTypes<…, { merge: false }>()` because supabase-js assumes
 * an embed is an array without a generated Database type. See berths.ts. */
const COLUMNS =
  'id, company_id, name, type, length_m, width_m, draft_m, flag_code, service_needs, company:companies(id, name), created_at'

function clean(input: VesselInput): VesselInput {
  return {
    company_id: input.company_id,
    name: input.name.trim(),
    type: input.type,
    // Passed through as numbers. Nothing is nullable here — see the note on
    // Vessel.length_m — so there is no blank-means-unknown case to handle.
    length_m: input.length_m,
    width_m: input.width_m,
    draft_m: input.draft_m,
    flag_code: input.flag_code,
    service_needs: AMENITY_ORDER.filter((code) => input.service_needs.includes(code)),
  }
}

/**
 * Fleet first, then ship — the order the list is read in when you are looking
 * at who operates what. Sorting happens here rather than in the query because
 * the company name lives in an embed, and PostgREST cannot order a top-level
 * result by an embedded column. The query still orders by `name` so the rows
 * arrive in a stable order regardless.
 */
export function vesselComparator(a: Vessel, b: Vessel): number {
  const fleet = (v: Vessel) => v.company?.name ?? String(v.company_id)
  return fleet(a).localeCompare(fleet(b)) || a.name.localeCompare(b.name)
}

/**
 * A company's fleet. The company id is required, not left to RLS: a berth
 * owner can also read the ships booked at their berths
 * (20261004122159_owners_see_booked_vessels), so "every vessel I can see" is
 * no longer "my fleet".
 */
export async function listVessels(companyId: number): Promise<Vessel[]> {
  const { data, error } = await supabase
    .from('vessels')
    .select(COLUMNS)
    .eq('company_id', companyId)
    .order('name', { ascending: true })
    .overrideTypes<Vessel[], { merge: false }>()

  if (error) throw toDataError(error, 'Could not load vessels.')
  return (data ?? []).sort(vesselComparator)
}

/**
 * How many rows there are, without fetching any — the same HEAD request the
 * other counts use. See countBerths.
 */
export async function countVessels(): Promise<number> {
  const { count, error } = await supabase
    .from('vessels')
    .select('id', { count: 'exact', head: true })

  if (error) throw toDataError(error, 'Could not count vessels.')
  return count ?? 0
}

/** One vessel, for the detail and edit pages. Null when the id does not exist. */
export async function getVessel(id: number): Promise<Vessel | null> {
  const { data, error } = await supabase
    .from('vessels')
    .select(COLUMNS)
    .eq('id', id)
    .maybeSingle()
    .overrideTypes<Vessel, { merge: false }>()

  if (error) throw toDataError(error, 'Could not load the vessel.')
  return data
}

export async function createVessel(input: VesselInput): Promise<Vessel> {
  const { data, error } = await supabase
    .from('vessels')
    .insert(clean(input))
    .select(COLUMNS)
    .single()
    .overrideTypes<Vessel, { merge: false }>()

  if (error) throw toDataError(error, 'Could not create the vessel.')
  return data
}

export async function updateVessel(id: number, input: VesselInput): Promise<Vessel> {
  const { data, error } = await supabase
    .from('vessels')
    .update(clean(input))
    .eq('id', id)
    .select(COLUMNS)
    .single()
    .overrideTypes<Vessel, { merge: false }>()

  if (error) throw toDataError(error, 'Could not save the vessel.')
  return data
}

export async function deleteVessel(id: number): Promise<void> {
  const { error } = await supabase.from('vessels').delete().eq('id', id)
  if (error) throw toDataError(error, 'Could not delete the vessel.')
}
